/* TODO: should all be translated */

import { AnimatePresence, m, LazyMotion, domAnimation } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { Action, AnyAction, Dispatch } from "redux";
import { matchPath, useHistory, useParams } from "react-router-dom";

import { useAppDispatch, useTypedSelector } from "../../../store/reducers/use-typed-selector";
import { ChannelFeaturesEnum, FeatureFlagsEnum, LanguagesAbbr, NavProfileItems } from "../../../types/working-model";
import { PATHNAMES } from "../../../utils/admin-routing-utils";
import { ParamsProps } from "../../live-event/live-event";
import { eventPath } from "../../live-event/utils";
import { useTranslate } from "../../../i18n/useTranslationModules";
import useRouteMap from "../../live-event/hooks/use-route-map";
import { COOKIES_ACCEPTED_INDEX_DB } from "../../../types/gdpr";
import db from "../../../utils/datastore/indexed-db";
import { IRegistrationGDPR } from "../../../connection/registration";
import useCheckIfCookieAccepted from "./use-check-if-cookie-accepted";
import { OptionalComponent } from "../../../utils/optional-component";
import useTrackHeight from "../../../utils/use-track-height";
import { forceRecheckCookieStatus, confirmGDPRCookies } from "../../../store/actions/event/event-actions";

import './cookie-banner.scss';
import Icon, { COLORS, ICONS } from "../icon";

export const handleAcceptCookies = async (
	accepted: boolean,
	isEditor: boolean | undefined,
	eventUuid: string | undefined,
	dispatch: Dispatch<AnyAction | Action>,
	setClickedAccept: React.Dispatch<React.SetStateAction<boolean>> | null,
	userRegUUID: string | null,
	blProfileUserToken: string | undefined
) => {
	if (isEditor || !eventUuid) return; // do nothing in admin editor

	try {
		// Banner isn't shown on the profile page (and in the profile page the user can change their cookie consent)
		setClickedAccept && setClickedAccept(accepted);

		const data: IRegistrationGDPR = { cookies_accepted: accepted, cookies_accepted_at: Date.now() };

		await db.putItem({
			type: COOKIES_ACCEPTED_INDEX_DB,
			uuid: eventUuid,
			data
		});

		dispatch(forceRecheckCookieStatus());

		if (userRegUUID && blProfileUserToken) {
			// update db registration gdpr
			dispatch(confirmGDPRCookies(blProfileUserToken, userRegUUID, data));
		}
	} catch (e) {
		console.error(e);
	}
};

interface ICookieBannerProps {
	forceShowBanner?: boolean; /** used for showing in admin */
	isEditor?: boolean;
	containerHeight?: (height: number) => void;
	forceHeightRecalcutation?: number;
	language?: LanguagesAbbr;
	eventName?: string;
}

const CookieBanner = ({
	forceShowBanner,
	isEditor,
	containerHeight,
	forceHeightRecalcutation,
	language,
	eventName,
}: ICookieBannerProps): JSX.Element | null => {
	const { t } = useTranslate("homepage");

	const debug = window.location.hostname === 'localhost';
	const RouteMap = useRouteMap({ debug });

	const dispatch = useAppDispatch();

	const cookieBannerRef = useRef<HTMLDivElement | null>(null);

	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);
	const featureFlags = useTypedSelector(state => state.FeatureFlagsReducer.featureFlags);
	const userRegUUID = useTypedSelector(state => state.LiveEventReducer.registrationId);
	const blProfileUserToken = useTypedSelector(state => state.LiveEventReducer.blProfileUserToken);
	const channelFeatures = useTypedSelector(state => state.AuthReducer?.channels?.find(c => c.channel === state.AuthReducer?.user?.active_channel)?.enabled_features);

	const [clickedAccept, setClickedAccept] = useState(false);

	const gdprFeatureFlagOn = !!featureFlags[FeatureFlagsEnum.event_cookie_settings];

	const event = workingEvent || eventBundle;

	const hasLandingPage = event?.homepage && event.settings.display_landing_page !== false;

	const eventUuid = event?.uuid;

	const cookieBannerEnabled = event?.settings?.gdpr?.enable_cookie_banner;

	const registrationOn = event?.registration_on;

	const { userRegGDPR, checkingUserCookieStatus } = useCheckIfCookieAccepted({ isEditor });

	const history = useHistory();

	const { language: paramsLanguage, eventName: paramsEventName, uuid: eventParamUuid } = useParams() as ParamsProps;

	const cookieBannerHeight = useTrackHeight({ ref: cookieBannerRef.current, forceRerender: forceHeightRecalcutation });
	useEffect(() => {
		containerHeight?.(cookieBannerHeight.height);
	}, [containerHeight, cookieBannerHeight]);

	const handleLearnMore = () => {
		const _language = language || paramsLanguage || 'en';
		const _eventName = eventName || paramsEventName;
		if (isEditor && eventUuid) {
			history.push(PATHNAMES.Event[NavProfileItems.PrivacyPolicy](eventUuid, _language));
		} else {
			const policyPath = `/${eventPath(_eventName, eventParamUuid)}/${_language}/privacy-policy`;
			window.open(policyPath, '_blank');
		}
	};

	/*
		1. Registration on with Landing page on, show cookies on landing page
		2. Registration on with Landing page off, show cookies on registration page 
		3. Registration off, goes straight into event page, show cookies on event page
	*/
	const pathname = history.location.pathname;
	const shouldDisplayOnThisPage = useCallback(() => {
		const matchedLanding = matchPath(pathname, {
			path: [
				PATHNAMES.Event.Landing,
				RouteMap.Landing,
				RouteMap.Sessions
			],
			exact: true,
		});

		const matchedRegistration = matchPath(pathname, {
			path: [
				PATHNAMES.Event.Registration,
				RouteMap.Registration,
			],
		});

		// To be shown persistently across landing, home, event, sessions, session
		const matchedPathsForNoRegistration = matchPath(pathname, {
			path: [
				PATHNAMES.Event.Landing,
				RouteMap.Landing,
				PATHNAMES.Event.HomePath,
				RouteMap.Home,
				PATHNAMES.Event.Event,
				PATHNAMES.Event.SessionsPath,
				RouteMap.Sessions,
				PATHNAMES.Session.Session,
				RouteMap.Session,
			],
			exact: true,
		});

		if (registrationOn && !userRegUUID) {
			if (matchedLanding && hasLandingPage) return true;
			if (matchedRegistration) return true;
		} else if (matchedPathsForNoRegistration) {
			return true;
		}

		return false;
	}, [RouteMap, hasLandingPage, pathname, registrationOn, userRegUUID]);

	// Doesn't show the cookie banner if IndexDB has some GDPR data for them already.
	useEffect(() => {
		if (isEditor) return;

		async function getData() {
			if (!eventUuid) return;
			const gdprAcceptedPreReg = await db.getItem<IRegistrationGDPR>(COOKIES_ACCEPTED_INDEX_DB, eventUuid);
			setClickedAccept(!!gdprAcceptedPreReg);
		}
		getData();
	}, [eventBundle, eventUuid, isEditor]);

	const shouldDisplay = (
		!checkingUserCookieStatus
		&& shouldDisplayOnThisPage()
		&& gdprFeatureFlagOn
		&& !clickedAccept
		&& (
			forceShowBanner
			|| (
				cookieBannerEnabled
				&& !userRegGDPR
			)
		)
	);

	const handleCookieDecision = (value: boolean) => {
		handleAcceptCookies(value, isEditor, eventUuid, dispatch, setClickedAccept, userRegUUID, blProfileUserToken);
		setClickedAccept(true);
	};

	const cookieOptInEnabled = isEditor
		? channelFeatures?.includes(ChannelFeaturesEnum.cookie_opt_in)
		: event?.channel_features?.includes(ChannelFeaturesEnum.cookie_opt_in);

	return (
		<AnimatePresence>
			<OptionalComponent display={!!shouldDisplay}>
				<LazyMotion features={domAnimation}>
					<m.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="cookie-banner-container"
						ref={cookieBannerRef}
					>
						<div className="cookie-banner-info">
							<div className="cookie-banner-title">
								{t("homepage:gdpr.This website uses cookies")}
							</div>
							<div className="cookie-banner-subtitle">
								{t("homepage:gdpr.This site uses cookies to deliver and enhance the quality of its services and to analyze traffic")}
								<span className="learn-more-link" onClick={handleLearnMore}> {t("homepage:gdpr.Learn more")}</span>
							</div>
						</div>
						<div className="cookie-banner-actions">
							<OptionalComponent display={cookieOptInEnabled}>
								<button onClick={() => handleCookieDecision(false)} className="learn-more">
									{t("homepage:gdpr.Opt Out")}
								</button>
							</OptionalComponent>
							<button onClick={() => handleCookieDecision(true)} className="accept">
								{cookieOptInEnabled
									? t('homepage:gdpr.Accept all cookies')
									: t('homepage:gdpr.Ok, Got it')}
							</button>

						</div>
					</m.div>
				</LazyMotion>
			</OptionalComponent>
		</AnimatePresence>
	);
};

export default CookieBanner;
